import { CommonModule } from '@angular/common';
import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { FlexLayoutModule } from '@angular/flex-layout';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import {MatDatepicker, MatDatepickerInput} from "@angular/material/datepicker";
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { DateConfig } from './booking-calendar.model';
import {LibDateUtilsService} from "@ui-kit/framework/util/service/date-utils.service";

@Component({
  selector: 'lib-booking-calendar',
  standalone: true,
  imports: [
    CommonModule,
    FlexLayoutModule,
    MatCardModule,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatDatepicker,
    MatDatepickerInput,
  ],
  templateUrl: './booking-calendar.component.html',
  styleUrl: './booking-calendar.component.scss',
})
export class BookingCalendarComponent implements OnInit, OnChanges, OnDestroy {
  @Input() bookButtonLabel: string = 'Book Selected';
  @Input() dateConfigs: Map<string, Map<string, DateConfig>> = new Map();
  @Input() disableUnavailableDates: boolean = true;
  @Input() disableDateIfEmpty: boolean = true; // can't select a date if map is empty for date
  @Input() disableConfirm: boolean = false;
  @Input() preSelectedDates: string[] = [];

  @Input() gridDataLoading: boolean = false;

  @Input() initGrid: boolean = false;
  @Output() initGridChange = new EventEmitter<boolean>();

  @Output() focusDateChange = new EventEmitter<Date>();

  // Internal date handling
  focusDate = new Date();
  selectedDates: string[] = [];
  isDragging = false;
  dragStart: string | null = null;
  dragEnd: string | null = null;
  hasMoved = false; // Track if mouse moved during drag

  // Preview selection during drag operations
  dragPreviewDates: string[] = [];

  showFullDates: boolean = false;
  calendarHeight: number = 610;

  // Global mouseup handler reference
  private globalMouseUpHandler = () => {
    if (this.isDragging) {
      this.handleMouseUp();
    }
  };

  dayHeaders = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
  monthNames = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"
  ];

  calendarDays: (number | null)[] = [];

  @Output() dateSelectionChange = new EventEmitter<string[]>();
  @Output() confirmSelected = new EventEmitter<string[]>();

  constructor(private dateUtils: LibDateUtilsService) {
  }

  ngOnInit() {
    if (this.preSelectedDates.length > 0) {
      this.selectedDates = this.preSelectedDates;
      const minDate = this.preSelectedDates.reduce((min, date) => new Date(date) < min ? new Date(date) : min, new Date(this.preSelectedDates[0] ?? new Date()));
      this.focusDate = new Date(minDate);
    }
    this.generateCalendarDays();

    // Add global mouseup listener to ensure drag state is always reset
    document.addEventListener('mouseup', this.globalMouseUpHandler);
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['preSelectedDates']) {
      if (this.preSelectedDates.length > 0) {
        this.selectedDates = this.preSelectedDates;
      }
    }

    if (changes['initGrid'] && this.initGrid) {
      const minDate = this.preSelectedDates
        .reduce((min, date) =>
            new Date(date) < min ? new Date(date) : min,
          new Date(this.preSelectedDates[0] ?? new Date()));
      this.focusDate = new Date(minDate);
      this.generateCalendarDays();
      this.initGridChange.emit(false);
    }
  }

  ngOnDestroy() {
    // Clean up global event listener
    document.removeEventListener('mouseup', this.globalMouseUpHandler);
  }

  generateCalendarDays() {
    const year = this.focusDate.getFullYear();
    const month = this.focusDate.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    this.calendarDays = [];

    // Add empty cells for days before month starts
    for (let i = 0; i < startingDayOfWeek; i++) {
      this.calendarDays.push(null);
    }

    // Add days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      this.calendarDays.push(day);
    }
  }

  formatDateString(day: number): string {
    const year = this.focusDate.getFullYear();
    const month = this.focusDate.getMonth();
    return this.dateUtils.displayShortDate(new Date(year, month, day))!;
  }

  getDateConfig(day: number): DateConfig | undefined {
    if (!this.focusDate || !day) return undefined;
    const monthString = this.dateUtils.displayMonthAndYear(this.focusDate);
    const dateString = this.formatDateString(day);
    return this.dateConfigs.get(monthString!)?.get(dateString) ?? undefined;
  }

  isDateAvailable(day: number): boolean {
    const config = this.getDateConfig(day);
    // If there's a config, use its unavailable state
    if (config !== undefined) {
      return !config.unavailable;
    }
    return !this.disableDateIfEmpty;
  }

  getDateLabel(day: number): string {
    const config = this.getDateConfig(day);
    if (config?.label) {
      return config.label;
    }
    return '';
  }

  handleMouseDown(day: number | null) {
    if (!day || (this.disableUnavailableDates && !this.isDateAvailable(day))) return;

    const dateString = this.formatDateString(day);

    // Start dragging - clear existing selection and start fresh
    this.isDragging = true;
    this.dragStart = dateString;
    this.dragEnd = dateString;
    this.hasMoved = false;
  }

  handleMouseEnter(day: number | null) {
    if (!day || !this.isDragging || !this.dragStart || !this.isDateAvailable(day)) return;

    const dateString = this.formatDateString(day);
    this.dragEnd = dateString;
    this.hasMoved = true; // Mark that we've moved during drag

    // Generate preview selection for visual feedback during drag
    this.dragPreviewDates = this.generateDateRange(this.dragStart, dateString);
  }

  resetDrag() {
    this.isDragging = false;
    this.dragStart = null;
    this.dragEnd = null;
    this.hasMoved = false;
    this.dragPreviewDates = [];
  }

  handleMouseUp() {
    // End dragging - always reset state
    this.resetDrag();
  }

  handleMouseUpOnDay(day: number | null) {
    if (!day) {
      this.resetDrag();
      return;
    }

    if (this.disableUnavailableDates && !this.isDateAvailable(day)) {
      this.resetDrag();
      return;
    }

    const dateString = this.formatDateString(day);

    if (!this.hasMoved || !this.isDragging) {
      // Simple click: toggle the day
      if (this.selectedDates.includes(dateString)) {
        this.selectedDates = this.selectedDates.filter(d => d !== dateString);
      } else {
        this.selectedDates = [...this.selectedDates, dateString];
      }
    } else if (this.dragStart) {
      // Dragged selection: commit preview range from start to mouseup day
      if (!this.hasMoved) {
        // No movement: select only this day
        this.selectedDates = [...this.selectedDates, dateString];
      } else {
        const dates = this.generateDateRange(this.dragStart, dateString);
        this.selectedDates = [...new Set([...this.selectedDates, ...dates])];
      }
    }

    this.dateSelectionChange.emit(this.selectedDates);

    // Reset drag state and preview
    this.resetDrag();
  }

  handleMouseLeave() {
    // End dragging if mouse leaves the calendar area
    if (this.isDragging) {
      this.resetDrag();
    }
  }

  private generateDateRange(startDate: string, endDate: string): string[] {
    const dates: string[] = [];
    const start = new Date(startDate);
    const end = new Date(endDate);

    // Determine the actual start and end (in case user dragged backwards)
    const actualStart = start < end ? start : end;
    const actualEnd = start < end ? end : start;

    // Generate all dates in the range (inclusive)
    for (let d = new Date(actualStart); d <= actualEnd; d.setDate(d.getDate() + 1)) {
      const monthString = this.dateUtils.displayMonthAndYear(d);
      const dateString = this.dateUtils.displayShortDate(d)!;
      const dateConfig = this.dateConfigs.get(monthString!)?.get(dateString) ?? undefined;
      if (dateConfig && dateString && !(this.disableUnavailableDates && dateConfig.unavailable)) {
        dates.push(dateString);
      }
    }

    return dates;
  }

  previousMonth() {
    this.focusDate = new Date(this.focusDate.getFullYear(), this.focusDate.getMonth() - 1);
    this.focusDateChange.emit(this.focusDate);
    this.generateCalendarDays();
  }

  nextMonth() {
    this.focusDate = new Date(this.focusDate.getFullYear(), this.focusDate.getMonth() + 1);
    this.focusDateChange.emit(this.focusDate);
    this.generateCalendarDays();
  }

  clearSelection() {
    this.selectedDates = [];
    this.dateSelectionChange.emit(this.selectedDates);
  }

  onConfirmSelected() {
    this.confirmSelected.emit(this.selectedDates);
  }

  getSelectedDatesPreview(): string {
    const sortedDates = this.selectedDates.sort();
    const preview = sortedDates
      .slice(0, 3)
      .map((d) => new Date(d).toLocaleDateString())
      .join(", ");
    return sortedDates.length > 3 ? `${preview} +${sortedDates.length - 3} more` : preview;
  }

  getSelectedDatesFull(): string {
    const sortedDates = this.selectedDates.sort();
    return sortedDates
      .map((d) => new Date(d).toLocaleDateString())
      .join(", ");
  }

  setSelectedDates(dates: string[]) {
    this.focusDate = new Date(dates[0]);
    this.generateCalendarDays();
    this.selectedDates = dates;
  }

  selectMonth(event: any, picker: MatDatepicker<any>) {
    this.focusDate = event;
    this.focusDateChange.emit(this.focusDate);
    this.generateCalendarDays();
    picker.close();
  }

  toggleFullSelectedDates() {
    this.showFullDates = !this.showFullDates;

    const dateLines = Math.ceil(this.selectedDates.length / 5);
    this.calendarHeight = this.showFullDates ? 610 + (dateLines - 1) * 13 : 610;
  }
}

